// src/components/energy-reset/BadgeStrip.tsx
import { API_BASE } from "../../utils/api";
import type { Badge, BadgeAward } from "../../api/badges";

type Props = {
  badges: Badge[];
  xp: number;
  day: number;
  awards: BadgeAward[];
  onBadgeClick: (b: Pick<Badge, "code" | "label">) => void;
};

function assetUrl(id?: string | null) {
  return id ? `${API_BASE}/assets/${id}` : null;
}

export default function BadgeStrip({ badges, xp: _xp, day: _day, awards, onBadgeClick }: Props) {
  return (
    <div className="w-full">
      {/* Авто-подбор колонок: плитка минимум ~112px, иначе растягивается на всю доступную ширину */}
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))" }}
      >
        {badges.map((b) => {
          // Получен ли этот бейдж (есть запись в badge_awards)
          const got = awards.some((a) => String(a.code) === String(b.code));
          // Новая логика: по умолчанию всё закрыто, пока не получено
          const locked = !got;

          const title = got ? `${b.label} • получен` : `${b.label} • закрыт`;

          const img = assetUrl(b.image);

          return (
            <button
              key={b.code}
              id={`badge-${b.code}`} // 🔹 якорь для FLIP/поиска узла
              type="button"
              data-badge-code={b.code} // 🔹 данные для анимаций/поиска
              data-badge-label={b.label}
              data-got={got ? "1" : "0"}
              data-locked={locked ? "1" : "0"}
              title={title}
              aria-label={title}
              aria-disabled={locked}
              tabIndex={got ? 0 : -1}
              onClick={() => {
                if (got) onBadgeClick({ code: b.code, label: b.label });
              }}
              className={[
                "w-full h-[64px] md:h-[56px] px-2 py-2",
                "rounded-xl border transition-colors",
                "flex items-center gap-2", // компактная плитка
                got
                  ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                  : "bg-gray-50 border-gray-200 text-gray-600",
                got ? "cursor-pointer" : "cursor-default",
              ].join(" ")}
            >
              {/* Иконка/картинка бейджа */}
              <span
                id={`badge-thumb-${b.code}`} // 🔹 отдельный якорь для точного FLIP от «кружка»
                data-badge-thumb // 🔹 маркер миниатюры
                className={[
                  "inline-flex shrink-0 items-center justify-center rounded-full",
                  "h-7 w-7",
                  got ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-600",
                ].join(" ")}
                aria-hidden
                style={{ willChange: "transform" }} // 🔹 хинт для плавного FLIP
              >
                {got ? (
                  // Если бейдж получен — показываем картинку (если есть) или эмодзи медали
                  img ? (
                    <img
                      src={img}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    "🏅"
                  )
                ) : (
                  // До получения всегда показываем замок
                  "🔒"
                )}
              </span>

              {/* Текст */}
              <div className="min-w-0 leading-tight text-left">
                <div className="text-[11px] font-semibold truncate">{b.label}</div>
                <div className="text-[10px] opacity-60 truncate">
                  {got ? "получен" : "закрыт"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
